import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const policies = {
  privacy: {
    title: "Privacy Policy",
    lastUpdated: "February 2026",
    content: [
      {
        heading: "Information We Collect",
        text: `When you visit our website or make a purchase, we collect certain information about you, including:

• Personal information: Name, email address, phone number, shipping address, and billing information when you place an order.
• Payment information: Processed securely through our payment partners (Stripe). We do not store your full credit card details.
• Usage data: Information about how you interact with our website, including pages visited, time spent, and referring sources.
• Device information: Browser type, operating system, and device identifiers.`
      },
      {
        heading: "How We Use Your Information",
        text: `We use the information we collect to:

• Process and fulfill your orders
• Communicate with you about your orders and our products
• Send promotional emails (with your consent)
• Improve our website and customer experience
• Comply with legal obligations`
      },
      {
        heading: "Information Sharing",
        text: `We do not sell your personal information. We may share your information with:

• Service providers who help us operate our business (payment processors, shipping carriers)
• Legal authorities when required by law
• Business partners with your explicit consent`
      },
      {
        heading: "Your Rights",
        text: `You have the right to:

• Access the personal information we hold about you
• Request correction of inaccurate information
• Request deletion of your information
• Opt out of marketing communications
• Withdraw consent at any time

To exercise these rights, please contact us at info@reggaeessences.com`
      },
      {
        heading: "Cookies",
        text: `We use cookies and similar technologies to improve your browsing experience, analyze website traffic, and personalize content. You can control cookie settings through your browser.`
      },
      {
        heading: "Security",
        text: `We implement appropriate security measures to protect your personal information. However, no method of transmission over the internet is 100% secure.`
      },
      {
        heading: "Contact Us",
        text: `If you have questions about this Privacy Policy, please contact us at info@reggaeessences.com`
      }
    ]
  },
  shipping: {
    title: "Shipping Policy",
    lastUpdated: "February 2026",
    content: [
      {
        heading: "Processing Time",
        text: `All orders are processed within 1-3 business days. Orders placed on weekends or holidays will be processed the next business day.

During peak seasons (holidays, sales), processing may take an additional 1-2 days.`
      },
      {
        heading: "Shipping Options",
        text: `We offer the following shipping options within the United States:

• Standard Shipping: 5-7 business days - FREE on orders over $35
• Priority Shipping: 2-3 business days - $8.99

Shipping times are estimates and not guaranteed. Delays may occur due to carrier issues or weather conditions.`
      },
      {
        heading: "Order Tracking",
        text: `Once your order ships, you will receive a confirmation email with tracking information. You can track your order through:

• The link in your shipping confirmation email
• Your account dashboard (if you have an account)
• The carrier's website using your tracking number`
      },
      {
        heading: "Shipping Address",
        text: `Please ensure your shipping address is correct before placing your order. We are not responsible for orders shipped to incorrect addresses provided by the customer.

If you need to change your shipping address after placing an order, contact us immediately. We can only make changes before the order has shipped.`
      },
      {
        heading: "International Shipping",
        text: `Currently, we only ship within the United States. We are working on expanding our shipping options. Subscribe to our newsletter to be notified when international shipping becomes available.`
      },
      {
        heading: "Damaged or Lost Packages",
        text: `If your package arrives damaged:
• Take photos of the damage
• Contact us within 7 days of delivery
• We will arrange a replacement or refund

If your package appears lost:
• Check tracking information for updates
• Contact us if there's no movement for 7+ days
• We will investigate and resolve the issue`
      }
    ]
  },
  refunds: {
    title: "Refund Policy",
    lastUpdated: "February 2026",
    content: [
      {
        heading: "Our Commitment",
        text: `We want you to love your Reggae Essences products! Due to the handmade and personal nature of our products, we have specific guidelines for returns and refunds.`
      },
      {
        heading: "Returns",
        text: `• Unopened Products: May be returned within 14 days of delivery for a full refund (minus shipping costs). Products must be in original, sealed packaging.

• Opened Products: Due to hygiene and safety reasons, we cannot accept returns on opened or used products.

• Damaged/Defective Products: If your product arrives damaged or defective, contact us within 7 days with photos. We will send a replacement or issue a full refund.`
      },
      {
        heading: "How to Request a Return",
        text: `To initiate a return:

1. Contact us at info@reggaeessences.com with your order number
2. Include the reason for your return
3. Wait for approval and return instructions
4. Ship the item back in its original packaging

We will process your refund within 5-7 business days of receiving the returned item.`
      },
      {
        heading: "Refund Process",
        text: `Refunds will be issued to the original payment method. Please allow:

• 5-7 business days for us to process the return
• 5-10 business days for the refund to appear in your account (depending on your bank)

You will receive an email confirmation when your refund has been processed.`
      },
      {
        heading: "Exchanges",
        text: `We handle exchanges on a case-by-case basis. Please contact us to discuss exchange options. In most cases, we recommend returning the unwanted item and placing a new order.`
      },
      {
        heading: "Non-Refundable Items",
        text: `The following items are not eligible for refunds:

• Sale or clearance items (final sale)
• Gift cards
• Opened or used products
• Products returned after 14 days`
      },
      {
        heading: "Contact Us",
        text: `If you have any questions about our refund policy, please contact us at info@reggaeessences.com. We're here to help!`
      }
    ]
  },
  disclaimer: {
    title: "Ingredient Disclaimer",
    lastUpdated: "February 2026",
    content: [
      {
        heading: "External Use Only",
        text: `All Reggae Essences products (soaps, body butters, and candles) are intended for external use only. Do not ingest any of our products.`
      },
      {
        heading: "Natural Ingredients",
        text: `Our products are made with natural ingredients. While we strive to use the highest quality, skin-friendly ingredients, natural does not mean allergen-free.

Please review the ingredient list for each product carefully before use, especially if you have known allergies or sensitivities.`
      },
      {
        heading: "Patch Test Recommended",
        text: `If you have sensitive skin or are trying a new product, we recommend performing a patch test:

1. Apply a small amount to a discrete area of skin
2. Wait 24 hours
3. If no irritation occurs, proceed with normal use
4. Discontinue use if any irritation develops`
      },
      {
        heading: "Allergen Information",
        text: `Our products may contain or come into contact with common allergens including:

• Tree nuts and nut oils (coconut, shea, cocoa)
• Essential oils
• Fragrance oils
• Dairy (goat milk in some soaps)
• Beeswax (in some formulations)

If you have severe allergies, please contact us before ordering.`
      },
      {
        heading: "Not Medical Advice",
        text: `Our products are not intended to diagnose, treat, cure, or prevent any disease or medical condition. If you have a skin condition, consult with a healthcare professional before using new skincare products.`
      },
      {
        heading: "Candle Safety",
        text: `For candle products:

• Never leave a burning candle unattended
• Keep away from children and pets
• Place on a heat-resistant surface
• Trim wick to 1/4 inch before each use
• Discontinue use when 1/2 inch of wax remains`
      },
      {
        heading: "Storage",
        text: `To ensure product quality and longevity:

• Store in a cool, dry place away from direct sunlight
• Keep soaps dry between uses
• Seal body butters after each use
• Keep candles away from heat sources when not in use`
      }
    ]
  }
};

export const PoliciesPage = () => {
  const { type } = useParams();
  const policy = policies[type];

  if (!policy) {
    return (
      <div className="min-h-screen pt-28 pb-16 bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-3xl font-bold text-forest mb-4">
            Policy Not Found
          </h1>
          <Link to="/" className="text-gold hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="policies-page" className="min-h-screen pt-28 pb-16 bg-cream">
      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-warm-brown hover:text-forest transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mb-4">
          {policy.title}
        </h1>
        <p className="text-warm-brown">
          Last updated: {policy.lastUpdated}
        </p>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="space-y-10">
          {policy.content.map((section, index) => (
            <div key={index}>
              <h2 className="font-heading text-xl font-semibold text-forest mb-4">
                {section.heading}
              </h2>
              <div className="text-warm-brown leading-relaxed whitespace-pre-line">
                {section.text}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Policy Links */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-warm-brown/20">
        <h3 className="font-semibold text-forest mb-4">Other Policies</h3>
        <div className="flex flex-wrap gap-4">
          {Object.entries(policies).map(([key, value]) => (
            key !== type && (
              <Link
                key={key}
                to={`/policies/${key}`}
                className="text-gold hover:text-terracotta transition-colors"
              >
                {value.title}
              </Link>
            )
          ))}
        </div>
      </section>
    </div>
  );
};
